<template>
  <div class="style-imports-page">
    <CCard class="mb-3">
      <CCardHeader>Style Import Runner</CCardHeader>
      <CCardBody>
        <p class="text-muted mb-3">
          Upload the style price list CSV and run the full background import pipeline. This does not hold the request open while the long-running import finishes.
        </p>

        <div class="mb-3">
          <label class="field-label">Style CSV</label>
          <input type="file" class="form-control" accept=".csv,text/csv" @change="fileChanged($event, 'style')" />
          <small v-if="styleFile" class="text-muted d-block mt-1">{{ styleFile.name }}</small>
        </div>

        <div class="mb-3">
          <label class="field-label">License Designs CSV</label>
          <input type="file" class="form-control" accept=".csv,text/csv" @change="fileChanged($event, 'license')" />
          <small v-if="licenseFile" class="text-muted d-block mt-1">{{ licenseFile.name }}</small>
          <small class="text-muted d-block mt-1">If both files are uploaded, styles run first and the license design import runs at the end with replace mode.</small>
        </div>

        <div class="mb-3">
          <label class="field-label">UploadStyles Chunk Size</label>
          <input type="number" min="100" max="20000" step="100" class="form-control" v-model.number="chunkSize" />
          <!-- <small class="text-muted">This replaces the manual 10k / 12k split with automatic chunked runs.</small> -->
        </div>

        <div class="d-flex align-items-center" style="gap:10px;">
          <CButton color="primary" size="sm" :disabled="(!styleFile && !licenseFile) || uploading" @click="startImport">
            Start Import
          </CButton>
          <small-loader v-if="uploading"></small-loader>
        </div>

        <div v-if="errorText" class="alert alert-danger mt-3 mb-0">
          {{ errorText }}
        </div>
      </CCardBody>
    </CCard>

    <CCard v-if="activeRun" class="mb-3">
      <CCardHeader>Active Import</CCardHeader>
      <CCardBody>
        <div class="run-grid">
          <div class="run-stat">
            <div class="run-stat-label">Status</div>
            <div class="run-stat-value">{{ activeRun.status }}</div>
          </div>
          <div class="run-stat">
            <div class="run-stat-label">Step</div>
            <div class="run-stat-value">{{ activeRun.current_step || 'queued' }}</div>
          </div>
          <div class="run-stat">
            <div class="run-stat-label">Rows</div>
            <div class="run-stat-value">{{ activeRun.processed_rows || 0 }} / {{ activeRun.total_rows || 0 }}</div>
          </div>
          <div class="run-stat">
            <div class="run-stat-label">Chunks</div>
            <div class="run-stat-value">{{ activeRun.current_chunk || 0 }} / {{ activeRun.total_chunks || 0 }}</div>
          </div>
        </div>

        <div class="mt-3">
          <strong>Files:</strong> {{ fileSummary(activeRun) }}
        </div>
        <div v-if="activeRun.error_message" class="alert alert-danger mt-3 mb-0">
          {{ activeRun.error_message }}
        </div>
        <div class="mt-3">
          <label class="field-label">Run Log</label>
          <textarea class="form-control" rows="16" :value="activeRun.log || ''" readonly></textarea>
        </div>
      </CCardBody>
    </CCard>

    <CCard>
      <CCardHeader>Recent Imports</CCardHeader>
      <CCardBody>
        <table class="table table-sm" v-if="runs.length">
          <thead>
            <tr>
              <th>ID</th>
              <th>File</th>
              <th>Status</th>
              <th>Step</th>
              <th>Rows</th>
              <th>Chunks</th>
              <th>Started</th>
              <th>Finished</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="run in runs" :key="run.id" @click="selectRun(run)" style="cursor:pointer;">
              <td>{{ run.id }}</td>
              <td>{{ fileSummary(run) }}</td>
              <td>{{ run.status }}</td>
              <td>{{ run.current_step }}</td>
              <td>{{ run.processed_rows || 0 }} / {{ run.total_rows || 0 }}</td>
              <td>{{ run.current_chunk || 0 }} / {{ run.total_chunks || 0 }}</td>
              <td>{{ formatDate(run.started_at) }}</td>
              <td>{{ formatDate(run.finished_at) }}</td>
            </tr>
          </tbody>
        </table>
        <div v-else class="text-muted">No style imports yet.</div>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
var methods = {}

methods.fileChanged = function (event, type) {
  var files = event && event.target ? event.target.files : []
  var selected = files && files.length ? files[0] : null
  if (type === 'license') {
    this.licenseFile = selected
    return
  }
  this.styleFile = selected
}

methods.startImport = function () {
  if (!this.styleFile && !this.licenseFile) {
    return
  }

  var formData = new FormData()
  if (this.styleFile) {
    formData.append('file', this.styleFile)
  }
  if (this.licenseFile) {
    formData.append('license_file', this.licenseFile)
  }
  formData.append('chunk_size', this.chunkSize)

  this.uploading = true
  this.errorText = ''

  axios.post(this.$apiAdress + '/api/styles/import-runs?token=' + localStorage.getItem('api_token'), formData).then((response) => {
    this.activeRun = response.data
    this.styleFile = null
    this.licenseFile = null
    this.fetchRuns()
    this.startPolling()
  }).catch((error) => {
    if (error && error.response && error.response.data) {
      var payload = error.response.data
      if (payload.errors) {
        this.errorText = Object.keys(payload.errors).map(function (key) {
          return payload.errors[key].join(', ')
        }).join(' | ')
      } else if (payload.message) {
        this.errorText = payload.message
      } else {
        this.errorText = JSON.stringify(payload)
      }
    } else {
      this.errorText = 'Upload failed.'
    }
    this.$store.commit('axios_error', error)
  }).finally(() => {
    this.uploading = false
  })
}

methods.fetchRuns = function () {
  axios.get(this.$apiAdress + '/api/styles/import-runs?token=' + localStorage.getItem('api_token'))
    .then((response) => {
      this.runs = response.data || []
      if (!this.activeRun && this.runs.length) {
        this.activeRun = this.runs[0]
      }
    })
    .catch((error) => {
      this.$store.commit('axios_error', error)
    })
}

methods.refreshActiveRun = function () {
  if (!this.activeRun || !this.activeRun.id) {
    return
  }

  axios.get(this.$apiAdress + '/api/styles/import-runs/' + this.activeRun.id + '?token=' + localStorage.getItem('api_token'))
    .then((response) => {
      this.activeRun = response.data
      this.fetchRuns()
      if (this.activeRun.status === 'completed' || this.activeRun.status === 'failed') {
        this.stopPolling()
      }
    })
    .catch((error) => {
      this.$store.commit('axios_error', error)
      this.stopPolling()
    })
}

methods.startPolling = function () {
  this.stopPolling()
  this.pollTimer = setInterval(() => {
    this.refreshActiveRun()
  }, 5000)
}

methods.stopPolling = function () {
  if (this.pollTimer) {
    clearInterval(this.pollTimer)
    this.pollTimer = null
  }
}

methods.selectRun = function (run) {
  this.activeRun = run
  if (run.status !== 'completed' && run.status !== 'failed') {
    this.startPolling()
  } else {
    this.stopPolling()
  }
}

methods.formatDate = function (value) {
  if (!value) return ''
  return String(value).replace('T', ' ').slice(0, 19)
}

methods.fileSummary = function (run) {
  if (!run) return ''
  var parts = []
  if (run.style_original_file_name || (run.run_style_import && run.original_file_name)) {
    parts.push('Styles: ' + (run.style_original_file_name || run.original_file_name))
  }
  if (run.license_original_file_name) {
    parts.push('Licenses: ' + run.license_original_file_name)
  }
  return parts.length ? parts.join(' | ') : (run.original_file_name || '')
}

export default {
  name: 'StyleImports',
  data () {
    return {
      styleFile: null,
      licenseFile: null,
      chunkSize: 5000,
      uploading: false,
      errorText: '',
      runs: [],
      activeRun: null,
      pollTimer: null
    }
  },
  mounted () {
    this.fetchRuns()
  },
  beforeDestroy () {
    this.stopPolling()
  },
  methods: methods
}
</script>

<style scoped>
.style-imports-page {
  width: 100%;
  float: left;
}

.field-label {
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #5f7891;
}

.run-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.run-stat {
  padding: 12px;
  border: 1px solid #dce7f2;
  border-radius: 12px;
  background: #eff5fb;
}

.run-stat-label {
  font-size: 12px;
  text-transform: uppercase;
  color: #73879c;
}

.run-stat-value {
  font-size: 20px;
  font-weight: 800;
  color: #20374d;
}

@media (max-width: 991px) {
  .run-grid {
    grid-template-columns: 1fr;
  }
}
</style>
